import { freeze, produce } from 'immer'
import { Signal } from '../utils/signals'
import {
  FeatureFlags,
  FeatureFlagValues,
  getFeatureFlagKeys,
  parseFlag,
} from './types'
import { getDefault } from './defaults'

const LS_PREFIX = 'wistor_flags__'

class FeatureFlagsImpl implements FeatureFlags {
  public values = getAllValues()
  public readonly changeSignal = new Signal<FeatureFlagValues>()

  /**
   * Define properties for dynamically updating flags in dev console. Usage:
   * featureFlags.singleLogin = true
  */
  constructor() {
    for (const flag of getFeatureFlagKeys()) {
      Object.defineProperty(this, flag, {
        get() { return freeze(this.values[flag], true) },
        set(value: FeatureFlagValues[typeof flag]) {
          this.values = setFlagValue(flag, value, this.values)
          this.changeSignal.emit(this.values)
        },
        enumerable: true
      })
    }
  }

  reset() {
    clearLocalStorage()
    this.values = getAllValues()
    this.changeSignal.emit(this.values)
  }

  set<K extends keyof FeatureFlagValues>(flag: K, value: FeatureFlagValues[K]): void {
    this.values = setFlagValue(flag, value, this.values)
    this.changeSignal.emit(this.values)
  }

  /**
   * Convenience function to update nested featurflags. Usage:
   * ```js
   * featureFlags.mutate('selectableWidgets', w => {
   *   w.defaultPolicy = 'blocked'
   *   w.names.allowed.push('BBHTML')
   * })
   * ```
   */
  mutate<K extends keyof FeatureFlagValues>(flag: K, mutationFn: (prev: FeatureFlagValues[K]) => void): void {
    const next = produce(this.values[flag], mutationFn)
    this.values = setFlagValue(flag, next, this.values)
    this.changeSignal.emit(this.values)
  }
}

function setFlagValue<K extends keyof FeatureFlagValues>(
  flag: K,
  value: FeatureFlagValues[K],
  values: FeatureFlagValues
): FeatureFlagValues {
  const parsedValue = parseFlag(flag, value)
  const next = produce(values, (draft: FeatureFlagValues) => { draft[flag] = parsedValue })
  setFlagToLocalStorage(flag, parsedValue)
  return next
}

export function createFeatureFlags() {
  return new FeatureFlagsImpl()
}

function getFlagFromLocalStorage<K extends keyof FeatureFlagValues>(flag: K): null | FeatureFlagValues[K] {
  const key = LS_PREFIX + flag
  const item = localStorage.getItem(key)
  if (item == null) return null

  try {
    const something: unknown = JSON.parse(item)
    return parseFlag(flag, something)
  } catch(e) {
    console.error(e)
    return null
  }
}

function getAllValues(): FeatureFlagValues {
  const entries = getFeatureFlagKeys().map(flag => [
    flag, getFlagFromLocalStorage(flag) ?? getDefault(flag)
  ] as const)
  return Object.fromEntries(entries) as FeatureFlagValues
}

function setFlagToLocalStorage<K extends keyof FeatureFlagValues>(
  flag: K,
  value: FeatureFlagValues[K]
) {
  const key = LS_PREFIX + flag
  localStorage.setItem(key, JSON.stringify(value))
}

function clearLocalStorage() {
  for (const flag of getFeatureFlagKeys()) {
    const key = LS_PREFIX + flag
    localStorage.removeItem(key)
  }
}
